import React, { useCallback, useState } from 'react';
import { Button, message } from 'antd';
import { useDocumentContext, COMPLEX_DOCUMENT_CONTEXT_KEYS } from '../../../context/complexDocumentContext';
import { useArtboardContext, CONTEXT_KEYS } from '../../../context/complexArtboardContext';
import { VERSION_CONTEXT_KEYS, useVersionContext } from '../../../context/complexVersionContext';
import { COMPLEX_DOCUMENT_ENVIRONMENT, COMPLEX_DESIGN_TOOLBAR } from '../translationValues';
import { TEMPLATE_ENVIRONMENT_PAGE } from '../../../artboard/PublishOverlay/translationValues';
import { useSessionContext } from '../../../context/sessionContext';
import { publishDesign, sendToTesting, moveToQaComplete } from '../../../utils/services/complexDocumentsAPI';
import { CreateTags } from './CreateTags';
import { VERSION_APPROVAL_PROCESS } from '../translationValues';
import { useTranslation } from 'react-i18next';
import { emailSendToTesting } from '../../../utils/services/complexEmailAPI';
import { moveToQaComplete as emailMoveToQaComplete, publishDesign as publishEmailDesign } from '../../../utils/services/complexEmailAPI';
import { getTimeStampDetails } from '../../../utils/helper';
import { RejectionCommentPopup } from '../ApprovalProcess/RejectionCommentPopup'
import { VersioningProcess } from '../VersionSettings/styles';
import { COMPLEX_DOCUMENT_VERSION, DESIGN_TYPE } from '../../../utils/complexDocumentConstants';

const NAMESPACES = ['VERSION_APPROVAL_PROCESS'];

export const ApprovalProcess = ({isEmail = false}) => {
   
  const { t } = useTranslation(NAMESPACES);
  const [showTestingTagsPopup, setShowTestingTagsPopup] = useState(false);
  const [showPublishingTagsPopup, setShowPublishingTagsPopup] = useState(false);
  const [sendForTestLoading, setSendForTestLoading] = useState(false);
  const [approvalLoading, setApprovalLoading] = useState(false);
  const [publishingLoading, setPublishingLoading] = useState(false);
  const [showRejectionPopup, setShowRejectionPopup] = useState(false);
  const [showRejectedButton, setShowRejectedButton] = useState(false);

  const blocksNotFit = document.getElementsByClassName('block-not-fit');
  const isDocumentHasError = blocksNotFit.length > 0;

  const currentDesignSelector = useCallback(store => store[CONTEXT_KEYS.CURRENT_DESIGN], []);
  const [currentDesign] = useArtboardContext(currentDesignSelector, `ComplexDocumentName`);

  const draftEditableSelector = useCallback(store => store[CONTEXT_KEYS.IS_DRAFT_EDITABLE], []);
  const [isDraftEditable] = useArtboardContext(draftEditableSelector, `ApprovalProcess`);

  const customCurrentBlockSelector = useCallback(store => store[CONTEXT_KEYS.CURRENT_BLOCK], []);
  const [currentEditingBlock] = useArtboardContext(customCurrentBlockSelector);

  const interactiveDesignSelector = useCallback(store => store[CONTEXT_KEYS.IS_INTERACTIVE_DESIGN], []);
  const [isInteractiveDesign] = useArtboardContext(interactiveDesignSelector, `ApprovalProcess`);

  const customSelector = useCallback(
    store => ({
      resourceName: store[COMPLEX_DOCUMENT_CONTEXT_KEYS.DOCUMENT_NAME],
      resourceId: store[COMPLEX_DOCUMENT_CONTEXT_KEYS.ID],
      communicationId: store[COMPLEX_DOCUMENT_CONTEXT_KEYS.COMMUNICATION_ID],
    }),
    [],
  );
  const [docInfo] = useDocumentContext(customSelector, `ComplexDocumentName`);


  const lifecycleSelector = useCallback(
    store => ({
      lifeCycle: store[VERSION_CONTEXT_KEYS.LIFECYCLE_TIMELINE],
      currentVisibleVersion: store[VERSION_CONTEXT_KEYS.CURRENT_VISIBLE_VERSION],
      tagsList: store[VERSION_CONTEXT_KEYS.TAGS_LIST],
      timelineList: store[VERSION_CONTEXT_KEYS.TIMELINE_LIST],
      versionForceRender: store[VERSION_CONTEXT_KEYS.VERSION_FORCE_RENDER],
    })
  )

  const [lifeCycleDetails, updateVersionStore] = useVersionContext(lifecycleSelector, `ComplexDocumentName`);

  const sessionSelector = useCallback(store => store.userData, []);
  const [userData] = useSessionContext(sessionSelector);

  const versionSettingsVisibleSelector = useCallback(
    store => ({
      visible: store[VERSION_CONTEXT_KEYS.VERSION_SETTINGS_VISIBLE],
    }),
    [],
  );
  const [{ visible }] = useVersionContext(versionSettingsVisibleSelector);

  const designType = currentDesign === DESIGN_TYPE.DOCUMENT ? "document" : currentDesign === DESIGN_TYPE.FLOW ? 'Reusable Page' : "Reusable section";
  const documentId = docInfo.resourceId;  

  const data = lifeCycleDetails.lifeCycle;


  const showSendToTesting = lifeCycleDetails.lifeCycle[COMPLEX_DOCUMENT_VERSION.DRAFT]
    ? lifeCycleDetails.lifeCycle[COMPLEX_DOCUMENT_VERSION.QA_READY]
      ? new Date(lifeCycleDetails.lifeCycle[COMPLEX_DOCUMENT_VERSION.DRAFT]) >
      lifeCycleDetails.lifeCycle[COMPLEX_DOCUMENT_VERSION.QA_READY]
      : true
    : false;

  const isQaReadyLatest = lifeCycleDetails.lifeCycle[COMPLEX_DOCUMENT_VERSION.QA_READY]
    ? lifeCycleDetails.lifeCycle[COMPLEX_DOCUMENT_VERSION.QA_COMPLETE]
      ? lifeCycleDetails.lifeCycle[COMPLEX_DOCUMENT_VERSION.QA_READY] > lifeCycleDetails.lifeCycle[COMPLEX_DOCUMENT_VERSION.QA_COMPLETE]
      : true
    : false;

  const isQaCompleteLatest = !isQaReadyLatest && lifeCycleDetails.lifeCycle[COMPLEX_DOCUMENT_VERSION.QA_COMPLETE]
    ? lifeCycleDetails.lifeCycle[COMPLEX_DOCUMENT_VERSION.PUBLISHED]
      ? lifeCycleDetails.lifeCycle[COMPLEX_DOCUMENT_VERSION.QA_COMPLETE] > lifeCycleDetails.lifeCycle[COMPLEX_DOCUMENT_VERSION.PUBLISHED]
      : true
    : false;
  const isRejected =
    lifeCycleDetails.currentVisibleVersion === COMPLEX_DOCUMENT_VERSION.QA_READY &&
    lifeCycleDetails.lifeCycle['rejected'];
  
  const checkUserAccess = (userType, roles, userDetails = {}) => {
    if (roles) {
      return (
        userDetails?.userType === userType && userDetails.roles.some(role => roles.includes(role))
      );
    } else {
      return userDetails?.userType === userType;
    }
  };

  const publishAccess = checkUserAccess('master', ['PUBLISHER'], userData) || checkUserAccess('client', ['PUBLISHER'], userData);

  const onSendForTesting = () => {
    const isQaVersionPresent = lifeCycleDetails.lifeCycle[COMPLEX_DOCUMENT_VERSION.QA_READY] ? true : false;
    if (isQaVersionPresent) {
      setShowTestingTagsPopup(true);
    }
    else {
      handleSendForTesting();
    }
  }

  const handleSendForTesting =useCallback(async () => {
    setSendForTestLoading(true);
    if (isDocumentHasError) {
      return message.error(
        t(
          COMPLEX_DESIGN_TOOLBAR.ERROR_IN_DOCUMENT.key,
          COMPLEX_DESIGN_TOOLBAR.ERROR_IN_DOCUMENT.value,
        ),
        6,
      );
    }    
    const response = isEmail? await emailSendToTesting(documentId, currentDesign): await sendToTesting(documentId, currentDesign);
    setSendForTestLoading(false);
    const statusCode = [2601, 728, 1904];
    if (statusCode.includes(response?.statusCode)) {
      getTimeStampDetails(documentId, updateVersionStore, currentDesign, isEmail)
      // TODO : Handle for flows / blocks
      message.success(
        t(
          COMPLEX_DOCUMENT_ENVIRONMENT.MOVED_TO_TESTING_SUCCESSFULLY_MESSAGE.key,
          COMPLEX_DOCUMENT_ENVIRONMENT.MOVED_TO_TESTING_SUCCESSFULLY_MESSAGE.value,
        ),
      );
      // TODO : Only on success
      updateVersionStore({
        [VERSION_CONTEXT_KEYS.VERSION_SETTINGS_VISIBLE]: true,
        [VERSION_CONTEXT_KEYS.VERSION_FORCE_RENDER]: lifeCycleDetails.versionForceRender + 1,
      });
      updateVersionStore({
        [VERSION_CONTEXT_KEYS.LIFECYCLE_TIMELINE]: Object.assign({}, lifeCycleDetails.lifeCycle, {
          [COMPLEX_DOCUMENT_VERSION.QA_READY]: new Date(),
        }),
        [VERSION_CONTEXT_KEYS.CURRENT_VISIBLE_VERSION]: COMPLEX_DOCUMENT_VERSION.QA_READY
      });
      setShowTestingTagsPopup(false);
    } else {
      message.error(
        t(
          COMPLEX_DOCUMENT_ENVIRONMENT.DEFAULT_ERROR_MESSAGE.key,
          COMPLEX_DOCUMENT_ENVIRONMENT.DEFAULT_ERROR_MESSAGE.value,
        ),
      );
      setShowTestingTagsPopup(false);
    }
  },[docInfo, currentDesign, lifeCycleDetails.lifeCycle]);

  const onRequest = useCallback(async () => {
    setApprovalLoading(true);
    let response = isEmail? await emailMoveToQaComplete(documentId,currentDesign) : await moveToQaComplete(documentId, currentDesign);
    setApprovalLoading(false);
    if (response) {
      if (response && response.errors && response.errors.length > 0) {
        console.error("Error in API :: ", response);
        message.error(t(
          TEMPLATE_ENVIRONMENT_PAGE.UNABLE_TO_PROCESS_ERROR_MESSAGE.key,
          TEMPLATE_ENVIRONMENT_PAGE.UNABLE_TO_PROCESS_ERROR_MESSAGE.value,
        ));
      } else {
        getTimeStampDetails(documentId, updateVersionStore, currentDesign, isEmail)
        message.success(t(
          TEMPLATE_ENVIRONMENT_PAGE.SENT_TO_APPROVAL_MESSAGE.key,
          TEMPLATE_ENVIRONMENT_PAGE.SENT_TO_APPROVAL_MESSAGE.value, { type: designType }
        ));
         updateVersionStore({
          [VERSION_CONTEXT_KEYS.CURRENT_VISIBLE_VERSION]: COMPLEX_DOCUMENT_VERSION.QA_READY
        });
      }
    }
  }, [documentId, lifeCycleDetails.lifeCycle, currentDesign]);

  const onPublish = () => {
    const isPublishedVersionPresent = lifeCycleDetails.lifeCycle[COMPLEX_DOCUMENT_VERSION.PUBLISHED] ? true : false;
    if (isPublishedVersionPresent) {
      setShowPublishingTagsPopup(true);
    }
    else {
      setPublishingLoading(true);
      handlePublishProcess();
    }
  }

  const handlePublishProcess = useCallback(async () => {
    
    setPublishingLoading(true);
    const response = isEmail? await publishEmailDesign(documentId,currentDesign):await publishDesign(documentId, currentDesign);
    if (response) {
      if (response && response.errors && response.errors.length > 0) {
        console.error("Error in API :: ", response);
        message.error(t(
          TEMPLATE_ENVIRONMENT_PAGE.UNABLE_TO_PROCESS_ERROR_MESSAGE.key,
          TEMPLATE_ENVIRONMENT_PAGE.UNABLE_TO_PROCESS_ERROR_MESSAGE.value,
        ));
      } else {
        getTimeStampDetails(documentId, updateVersionStore, currentDesign, isEmail)
        message.success(t(
          TEMPLATE_ENVIRONMENT_PAGE.PUBLISHED_SUCCESSFULLY_MESSAGE.key,
          TEMPLATE_ENVIRONMENT_PAGE.PUBLISHED_SUCCESSFULLY_MESSAGE.value, { type: designType }
        ));
        updateVersionStore({
          [VERSION_CONTEXT_KEYS.CURRENT_VISIBLE_VERSION]: COMPLEX_DOCUMENT_VERSION.PUBLISHED
        });
      }
    setPublishingLoading(false);
    }
  }, [documentId, lifeCycleDetails.lifeCycle, currentDesign]);

  const onReject = () => {
    setShowRejectionPopup(true);
  }

  return (
    <VersioningProcess>
      {(!isInteractiveDesign && isDraftEditable) && <div>
        {lifeCycleDetails.currentVisibleVersion === COMPLEX_DOCUMENT_VERSION.DRAFT && showSendToTesting && !currentEditingBlock &&
          <Button className='versioning-btn-style' loading={sendForTestLoading} onClick={() => { onSendForTesting() }}>
            {t(
              VERSION_APPROVAL_PROCESS.SEND_FOR_TESTING.key,
              VERSION_APPROVAL_PROCESS.SEND_FOR_TESTING.value,
            )}
            {showTestingTagsPopup &&
              <CreateTags t={t} showPublishingTagsPopup={showPublishingTagsPopup} showTestingTagsPopup={showTestingTagsPopup} setShowTestingTagsPopup={setShowTestingTagsPopup}
                handleSendForTesting={handleSendForTesting} handlePublishProcess={handlePublishProcess} updateVersionStore={updateVersionStore} currentDesign={currentDesign} documentId={documentId} />
            }
          </Button>
        }
      </div>}
      {visible ?
        <>
          {!isRejected && isQaReadyLatest && lifeCycleDetails.currentVisibleVersion === COMPLEX_DOCUMENT_VERSION.QA_READY &&
            <Button  className='versioning-btn-style' loading={approvalLoading} onClick={() => { onRequest() }}>
              {t(
                VERSION_APPROVAL_PROCESS.REQUEST_FOR_APPROVAL.key,
                VERSION_APPROVAL_PROCESS.REQUEST_FOR_APPROVAL.value,
              )}
            </Button>}
          {!publishAccess && !isRejected && isQaCompleteLatest && lifeCycleDetails.currentVisibleVersion === COMPLEX_DOCUMENT_VERSION.QA_READY &&
            <Button className='versioning-btn-style no-click'>
              {t(
                VERSION_APPROVAL_PROCESS.WAITING_FOR_APPROVAL.key,
                VERSION_APPROVAL_PROCESS.WAITING_FOR_APPROVAL.value,
              )}
            </Button>}
          {publishAccess && !isRejected && isQaCompleteLatest && lifeCycleDetails.currentVisibleVersion === COMPLEX_DOCUMENT_VERSION.QA_READY &&
            <div style={{ display: 'flex', flexDirection: 'row' }}>
              {!showRejectedButton &&
                <>
                  <Button className='versioning-btn-style' loading={publishingLoading} onClick={() => { onPublish() }}>
                    {t(
                      VERSION_APPROVAL_PROCESS.PUBLISH.key,
                      VERSION_APPROVAL_PROCESS.PUBLISH.value,
                    )}
                    {showPublishingTagsPopup &&
                      <CreateTags t={t} showPublishingTagsPopup={showPublishingTagsPopup} setShowPublishingTagsPopup={setShowPublishingTagsPopup} setShowTestingTagsPopup={setShowTestingTagsPopup} setSendForTestLoading={setSendForTestLoading}
                        handleSendForTesting={handleSendForTesting} handlePublishProcess={handlePublishProcess} updateVersionStore={updateVersionStore} currentDesign={currentDesign} documentId={documentId} />}
                  </Button>
                  <Button className='reject-btn' onClick={() => { onReject() }} style={{ borderRadius: '4px', marginRight: '6px', border:'none' }}>
                    {t(
                      VERSION_APPROVAL_PROCESS.REJECT.key,
                      VERSION_APPROVAL_PROCESS.REJECT.value,
                    )}
                    {showRejectionPopup && <RejectionCommentPopup isEmail={isEmail} t={t} setShowRejectedButton={setShowRejectedButton} showRejectionPopup={showRejectionPopup} setShowRejectionPopup={setShowRejectionPopup} documentId={documentId} currentDesign={currentDesign} />}
                  </Button>
                </>
              }
            </div>
          }
        </> : ''}
      {visible && isRejected &&
        <Button className='rejected-btn'>
          {t(
            VERSION_APPROVAL_PROCESS.REJECTED.key,
            VERSION_APPROVAL_PROCESS.REJECTED.value,
          )}
        </Button>
      }
    </VersioningProcess>
  );
};
